/**
 * Template Type Definitions
 * Defines the structure for pre-built layouts with manipulable layers
 */

import type { Layer } from './layers';
import type { BackgroundBrush, GradientStop } from './brush';
import { brushToCss } from './brush';

// Re-export GradientStop for convenience
export type { GradientStop };

// Template version
export const TEMPLATE_VERSION = '2.0' as const;

// Template categories
export type TemplateCategory = 'brand' | 'promotional' | 'social' | 'custom';

// Template metadata
export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  isBuiltin: boolean;
  thumbnail?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

// Canvas background uses the BackgroundBrush type
export type CanvasBackground = BackgroundBrush;

// Canvas configuration
export interface TemplateCanvas {
  baseWidth: number;
  baseHeight: number;
  background: CanvasBackground;
}

// Variable types for dynamic content
export type VariableType = 'text' | 'image' | 'color' | 'boolean';

// Template variable - dynamic content slots
export interface TemplateVariable {
  id: string; // 'headline', 'cta', 'subhead'
  name: string; // Display name
  type: VariableType;
  defaultValue: string;
  description?: string;
  required?: boolean;
  // Validation constraints
  maxLength?: number;
  minLength?: number;
  pattern?: string; // Regex pattern for validation
}

// Complete template definition
export interface Template {
  version: typeof TEMPLATE_VERSION;
  metadata: TemplateMetadata;
  canvas: TemplateCanvas;
  layers: Layer[];
  variables: TemplateVariable[];
}

// Template instance - a template with bound variable values
export interface TemplateInstance {
  templateId: string;
  variableValues: Record<string, string>;
  layerOverrides?: Record<string, Partial<Layer>>;
}

// Default variables commonly used in ad templates
export const DEFAULT_VARIABLES: TemplateVariable[] = [
  {
    id: 'headline',
    name: 'Headline',
    type: 'text',
    defaultValue: 'Your Privacy is Priceless',
    description: 'Main headline text',
    required: true,
    maxLength: 100,
  },
  {
    id: 'subhead',
    name: 'Subhead',
    type: 'text',
    defaultValue: "Don't let data brokers profit from your personal information",
    description: 'Supporting subheadline text',
    required: false,
    maxLength: 200,
  },
  {
    id: 'cta',
    name: 'Call to Action',
    type: 'text',
    defaultValue: 'Start Free Trial',
    description: 'Button text',
    required: true,
    maxLength: 30,
  },
];

// Type guard for template validation
export const isValidTemplate = (obj: unknown): obj is Template => {
  if (!obj || typeof obj !== 'object') return false;
  const t = obj as Partial<Template>;
  return (
    t.version === TEMPLATE_VERSION &&
    typeof t.metadata === 'object' &&
    typeof t.canvas === 'object' &&
    Array.isArray(t.layers) &&
    Array.isArray(t.variables)
  );
};

// Helper to create a new template
export const createTemplate = (
  metadata: Omit<TemplateMetadata, 'createdAt' | 'updatedAt'>,
  canvas: TemplateCanvas,
  layers: Layer[] = [],
  variables: TemplateVariable[] = DEFAULT_VARIABLES
): Template => ({
  version: TEMPLATE_VERSION,
  metadata: {
    ...metadata,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  canvas,
  layers,
  variables,
});

// Helper to get canvas background as CSS
export const getCanvasBackgroundCSS = (background: CanvasBackground): string => {
  return brushToCss(background);
};

// Resolve variable bindings in text
export const resolveVariables = (
  text: string,
  values: Record<string, string>
): string => {
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] ?? `{{${key}}}`);
};
